/**
 * Calendrier Paris (front) — étiquettes de jour `YYYY-MM-DD` en heure murale
 * Europe/Paris, indépendamment du fuseau du navigateur (les postes de test
 * de ce projet tournent par ex. en Europe/Istanbul — ne jamais se reposer
 * sur le fuseau local via `new Date(y, m, d)` ou `Date.now()` formaté sans
 * `timeZone` explicite).
 *
 * Module PUR (aucune dépendance Vue) : mutualise les helpers de calendrier
 * auparavant dupliqués dans plusieurs composables du panneau d'activité
 * (CC-01). Mêmes noms et même sémantique que l'équivalent serveur
 * (`server/utils/timezone.js`, fonctions `parisDayLabel`/`addDaysToLabel`)
 * pour qu'un lecteur qui passe du back au front retrouve les mêmes concepts
 * — non importé ici (code serveur, hors du bundle front), donc réimplémenté.
 */

export const PARIS_TZ = 'Europe/Paris'

/** Formateur hissé au niveau module : en construire un par appel coûte ~46× plus cher en boucle (mesuré ailleurs dans ce projet). */
const PARIS_DAY_LABEL_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: PARIS_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/**
 * Étiquette du jour calendaire Paris d'un instant, au format 'YYYY-MM-DD'.
 *
 * @param {Date|string|number} [dateLike=new Date()]
 * @returns {string}
 */
export function parisDayLabel(dateLike = new Date()) {
  return PARIS_DAY_LABEL_FMT.format(new Date(dateLike))
}

/**
 * Décale une étiquette de jour de N jours (N peut être négatif).
 * Arithmétique faite en UTC sur une date calendaire : insensible au
 * changement d'heure, contrairement à un décalage appliqué sur un instant local.
 *
 * @param {string} label - 'YYYY-MM-DD'
 * @param {number} days
 * @returns {string} 'YYYY-MM-DD'
 */
export function addDaysToLabel(label, days) {
  const d = new Date(`${label}T00:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

const DAY_LABEL_RE = /^\d{4}-\d{2}-\d{2}$/

/** Années acceptées pour une date saisie : une année en cours de frappe (« 0002 », « 0202 ») n'est pas une date à interroger. */
const MIN_USABLE_YEAR = 2000
const MAX_USABLE_YEAR = 2100

/**
 * `true` si `label` est un jour qui existe vraiment (`2026-04-31` non) et d'une
 * année plausible. Garde commune à la plage personnalisée, au calendrier et au
 * `?jour=` de l'adresse : une date refusée ici ne part jamais vers l'API.
 */
export function isUsableDayLabel(label) {
  if (typeof label !== 'string' || !DAY_LABEL_RE.test(label)) return false
  const [y, m, d] = label.split('-').map(Number)
  if (y < MIN_USABLE_YEAR || y > MAX_USABLE_YEAR) return false
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
}

/** `label` s'il est utilisable, sinon `null`. */
export function usableDayLabelOrNull(label) {
  return isUsableDayLabel(label) ? label : null
}
